/**
 * Prospects Admin Page — Demo & Quote requests from the public landing page.
 * Strictly separated from real customer accounts. From here the Admin reviews
 * the request and decides to create a real client account (manual process).
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Mail, Phone, Building2, Users, Sparkles, FileCheck2, Eye, MessageSquare,
  Calendar, Search, ArrowUpRight, ExternalLink, UserPlus, Copy, CheckCheck, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS = {
  new:               { label: 'Nouveau', cls: 'bg-blue-100 text-blue-700' },
  contacted:         { label: 'Contacté', cls: 'bg-amber-100 text-amber-700' },
  account_created:   { label: 'Compte créé', cls: 'bg-emerald-100 text-emerald-700' },
  won:               { label: 'Gagné', cls: 'bg-emerald-100 text-emerald-700' },
  lost:              { label: 'Perdu', cls: 'bg-slate-100 text-slate-600' },
};

const HEADCOUNT_LABEL = {
  '<50':       'Moins de 50',
  '50-200':    '50 – 200',
  '200-1000':  '200 – 1 000',
  '1000-5000': '1 000 – 5 000',
  '5000+':     '5 000+',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

// ──────────────────────────────────────────────────────────────────────
// Convert prospect → real account (Company + Admin user + Subscription)
// ──────────────────────────────────────────────────────────────────────
function genTempPassword() {
  const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%&*';
  const all = upper + lower + digits + symbols;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pwd = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
  for (let i = 0; i < 8; i++) pwd += pick(all);
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

function ConvertProspectDialog({ prospect, open, onClose, onConverted }) {
  const empty = {
    companyName: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    plan: 'free',
    isDemo: true,
    demoDurationDays: 14,
    sendWelcomeEmail: true,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // { companyId, loginEmail, plan, isDemo, demoExpiresAt, welcomeEmailSent }
  const [copied, setCopied] = useState(false);

  // Pre-fill when a prospect is provided
  useEffect(() => {
    if (prospect && open) {
      setForm({
        companyName: prospect.companyName || '',
        adminFirstName: prospect.firstName || '',
        adminLastName: prospect.lastName || '',
        adminEmail: (prospect.email || '').toLowerCase(),
        adminPassword: genTempPassword(),
        plan: prospect.requestType === 'quote' ? 'pro' : 'free',
        isDemo: true, // by default new accounts are demo accounts
        demoDurationDays: 14,
        sendWelcomeEmail: true,
      });
      setSuccess(null);
      setCopied(false);
    }
  }, [prospect, open]);

  if (!prospect) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.companyName.trim()) return 'Le nom d\'entreprise est requis';
    if (!form.adminFirstName.trim() || !form.adminLastName.trim()) return 'Prénom et nom requis';
    if (!/^\S+@\S+\.\S+$/.test(form.adminEmail)) return 'Email administrateur invalide';
    if (form.adminPassword.length < 8) return 'Mot de passe : 8 caractères minimum';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/prospects/${prospect.id}/convert`, form);
      if (r.data.alreadyConverted) {
        toast.warning('Ce prospect a déjà été converti.');
      } else {
        toast.success('Compte créé avec succès 🎉');
      }
      setSuccess({
        companyId: r.data.companyId,
        loginEmail: r.data.loginEmail || form.adminEmail,
        plan: r.data.plan || form.plan,
        password: form.adminPassword,
        isDemo: r.data.isDemo,
        demoExpiresAt: r.data.demoExpiresAt,
        welcomeEmailSent: r.data.welcomeEmailSent,
      });
      onConverted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la conversion');
    } finally { setBusy(false); }
  };

  const copyCreds = async () => {
    const txt = `Identifiants HR Engine
━━━━━━━━━━━━━━━━━━━━
Entreprise : ${form.companyName}
URL        : https://hrengine.app/login
Email      : ${success.loginEmail}
Mot de passe : ${success.password}

(À transmettre de manière sécurisée au client. Mot de passe à changer à la 1ère connexion.)`;
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      toast.success('Identifiants copiés !');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copie impossible'); }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onClose}>
      <DialogContent className="max-w-lg" data-testid="convert-prospect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-violet-600" />
            Convertir en compte client
          </DialogTitle>
          <DialogDescription>
            Crée l'entreprise, le compte administrateur et l'abonnement à partir des infos du prospect.
          </DialogDescription>
        </DialogHeader>

        {!success ? (
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-ink-700">Nom de l'entreprise *</label>
              <Input value={form.companyName} onChange={update('companyName')} data-testid="convert-company-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-ink-700">Prénom DRH *</label>
                <Input value={form.adminFirstName} onChange={update('adminFirstName')} data-testid="convert-first-name" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-ink-700">Nom DRH *</label>
                <Input value={form.adminLastName} onChange={update('adminLastName')} data-testid="convert-last-name" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-ink-700">Email administrateur *</label>
              <Input type="email" value={form.adminEmail} onChange={update('adminEmail')} data-testid="convert-email" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-ink-700">Mot de passe temporaire *</label>
              <div className="flex gap-2">
                <Input value={form.adminPassword} onChange={update('adminPassword')} data-testid="convert-password" className="font-mono text-xs" />
                <Button type="button" variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, adminPassword: genTempPassword() }))} data-testid="convert-regen-password">
                  ↻
                </Button>
              </div>
              <p className="text-[10px] text-ink-500">À transmettre au client. Il devra le changer à la 1ère connexion.</p>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-ink-700">Plan d'abonnement</label>
              <Select value={form.plan} onValueChange={(v) => setForm(f => ({ ...f, plan: v }))}>
                <SelectTrigger data-testid="convert-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">STARTER · Startups & PME (20 à 100 employés)</SelectItem>
                  <SelectItem value="pro">BUSINESS · Entreprises & groupes (+100 employés)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Demo toggle */}
            <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.isDemo}
                  onCheckedChange={(v) => setForm(f => ({ ...f, isDemo: !!v }))}
                  data-testid="convert-is-demo"
                />
                <span className="text-sm font-medium text-purple-900">
                  Période de démonstration limitée
                </span>
              </label>
              {form.isDemo && (
                <div className="ml-6 space-y-1">
                  <label className="text-[11px] text-purple-700">Durée</label>
                  <Select
                    value={String(form.demoDurationDays)}
                    onValueChange={(v) => setForm(f => ({ ...f, demoDurationDays: parseInt(v, 10) }))}
                  >
                    <SelectTrigger data-testid="convert-demo-duration" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 jours</SelectItem>
                      <SelectItem value="14">14 jours (recommandé)</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="60">60 jours</SelectItem>
                      <SelectItem value="90">90 jours</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-purple-600">
                    L'accès expirera automatiquement à cette date. Visible dans la liste des entreprises.
                  </p>
                </div>
              )}
            </div>

            {/* Welcome email */}
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <Checkbox
                checked={form.sendWelcomeEmail}
                onCheckedChange={(v) => setForm(f => ({ ...f, sendWelcomeEmail: !!v }))}
                data-testid="convert-send-email"
              />
              <span className="text-sm text-ink-700">
                Envoyer un email de bienvenue au DRH (sans le mot de passe, à transmettre séparément)
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="convert-cancel">Annuler</Button>
              <Button onClick={submit} disabled={busy} data-testid="convert-submit" className="bg-violet-600 hover:bg-violet-700">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création…</> : <>Créer le compte</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2" data-testid="convert-success">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                <CheckCheck className="h-5 w-5" />
                Compte créé avec succès
              </div>
              <dl className="space-y-1 text-xs text-ink-700">
                <div className="flex justify-between"><dt className="text-ink-500">Entreprise</dt><dd className="font-medium">{form.companyName}</dd></div>
                <div className="flex justify-between"><dt className="text-ink-500">Plan</dt><dd className="font-medium uppercase">{success.plan === 'pro' ? 'BUSINESS' : 'STARTER'}</dd></div>
                {success.isDemo && (
                  <div className="flex justify-between">
                    <dt className="text-ink-500">Démo expire</dt>
                    <dd className="font-medium text-purple-700">
                      {success.demoExpiresAt ? new Date(success.demoExpiresAt).toLocaleDateString('fr-FR') : '—'}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between"><dt className="text-ink-500">Email DRH</dt><dd className="font-medium font-mono text-[11px]">{success.loginEmail}</dd></div>
                <div className="flex justify-between items-baseline gap-2">
                  <dt className="text-ink-500 shrink-0">Mot de passe</dt>
                  <dd className="font-medium font-mono text-[11px] break-all text-right">{success.password}</dd>
                </div>
                {success.welcomeEmailSent && (
                  <div className="flex justify-between"><dt className="text-ink-500">Email envoyé</dt><dd className="font-medium text-emerald-700">✓ Bienvenue envoyé</dd></div>
                )}
              </dl>
            </div>
            <p className="text-xs text-ink-600">
              💡 Copiez les identifiants et transmettez-les au client par un canal sécurisé (signal/téléphone) — pas par email.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyCreds} data-testid="convert-copy-creds">
                {copied ? <><CheckCheck className="h-4 w-4 mr-2 text-emerald-600" /> Copié</> : <><Copy className="h-4 w-4 mr-2" /> Copier les identifiants</>}
              </Button>
              <Button onClick={onClose} data-testid="convert-close">Fermer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProspectDetail({ prospect, open, onClose, onUpdate }) {
  const [status, setStatus] = useState(prospect?.status || 'new');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(prospect?.status || 'new'); }, [prospect]);

  if (!prospect) return null;

  const updateStatus = async (newStatus) => {
    try {
      setBusy(true);
      await api.patch(`/leads/prospects/${prospect.id}`, { status: newStatus });
      setStatus(newStatus);
      toast.success('Statut mis à jour');
      onUpdate();
    } catch {
      toast.error('Erreur');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-brand" />
            {prospect.companyName}
          </DialogTitle>
          <DialogDescription>
            Demande {prospect.requestType === 'demo' ? 'de démo' : 'de devis'} reçue le {fmtDate(prospect.createdAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Contact */}
          <Card className="border-ink-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Contact</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-ink-700">
                <Users className="h-4 w-4 text-ink-400" />
                <span className="font-medium">{prospect.firstName} {prospect.lastName}</span>
                {prospect.role && <span className="text-ink-500">· {prospect.role}</span>}
              </div>
              <div className="flex items-center gap-2 text-ink-700">
                <Mail className="h-4 w-4 text-ink-400" />
                <a href={`mailto:${prospect.email}`} className="text-brand hover:underline">{prospect.email}</a>
              </div>
              {prospect.phone && (
                <div className="flex items-center gap-2 text-ink-700">
                  <Phone className="h-4 w-4 text-ink-400" />
                  <a href={`tel:${prospect.phone}`} className="hover:underline">{prospect.phone}</a>
                </div>
              )}
              <div className="flex items-center gap-2 text-ink-700">
                <Users className="h-4 w-4 text-ink-400" />
                <span>Effectifs : <b>{HEADCOUNT_LABEL[prospect.employeeCount] || prospect.employeeCount}</b></span>
              </div>
            </CardContent>
          </Card>

          {/* Message */}
          {prospect.message && (
            <Card className="border-ink-200 bg-ink-50/40">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-ink-500" /> Message du prospect
              </CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-ink-700 whitespace-pre-wrap">{prospect.message}</p>
              </CardContent>
            </Card>
          )}

          {/* Workflow */}
          <Card className="border-ink-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Workflow</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-ink-500 w-24">Statut actuel :</span>
                <Badge className={STATUS[status]?.cls}>{STATUS[status]?.label}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {['new', 'contacted', 'account_created', 'won', 'lost'].map(s => (
                  <Button key={s} variant={status === s ? 'default' : 'outline'} size="sm"
                    disabled={busy || status === s}
                    onClick={() => updateStatus(s)}
                    data-testid={`status-btn-${s}`}>
                    {STATUS[s].label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-ink-500 mt-2">
                ℹ️ Pour créer un vrai compte client, rendez-vous dans{' '}
                <a href="/super-admin" className="text-brand font-medium hover:underline inline-flex items-center gap-0.5">
                  SuperAdmin → Entreprises <ExternalLink className="h-3 w-3" />
                </a>{' '}
                puis marquez ce prospect comme "Compte créé".
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProspectsAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [toConvert, setToConvert] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.request_type = typeFilter;
      const { data } = await api.get('/leads/prospects', { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (p.companyName || '').toLowerCase().includes(s) ||
      (p.email || '').toLowerCase().includes(s) ||
      (p.firstName || '').toLowerCase().includes(s) ||
      (p.lastName || '').toLowerCase().includes(s)
    );
  });

  const stats = {
    total: items.length,
    new: items.filter(p => p.status === 'new').length,
    demo: items.filter(p => p.requestType === 'demo').length,
    quote: items.filter(p => p.requestType === 'quote').length,
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-7xl space-y-6" data-testid="prospects-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-ink-900 flex items-center gap-2">
              <Sparkles className="h-7 w-7 text-ai" />
              Prospects (Demandes Démo / Devis)
            </h1>
            <p className="mt-1 text-sm text-ink-500">
              Demandes reçues depuis la landing page publique. Distinctes des comptes clients existants.
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-500" />
            <div><div className="text-xs text-ink-500">Total prospects</div>
              <div className="text-2xl font-semibold text-ink-900">{stats.total}</div></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-ai" />
            <div><div className="text-xs text-ink-500">Nouveaux</div>
              <div className="text-2xl font-semibold text-ink-900">{stats.new}</div></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <Eye className="h-8 w-8 text-emerald-500" />
            <div><div className="text-xs text-ink-500">Demandes de démo</div>
              <div className="text-2xl font-semibold text-ink-900">{stats.demo}</div></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <FileCheck2 className="h-8 w-8 text-violet-500" />
            <div><div className="text-xs text-ink-500">Demandes de devis</div>
              <div className="text-2xl font-semibold text-ink-900">{stats.quote}</div></div>
          </CardContent></Card>
        </div>

        {/* Filters + list */}
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
            <CardTitle className="text-base">Liste des prospects</CardTitle>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-ink-400" />
                <Input placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-8 w-56" data-testid="prospect-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-36" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  <SelectItem value="demo">Démo</SelectItem>
                  <SelectItem value="quote">Devis</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-ink-500">Chargement…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-ink-200 rounded-xl">
                <Sparkles className="mx-auto h-12 w-12 text-ink-300" />
                <p className="mt-3 text-sm text-ink-600 font-medium">Aucun prospect pour le moment</p>
                <p className="text-xs text-ink-400 mt-1">Les demandes depuis la landing page apparaîtront ici.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
                    <tr>
                      <th className="text-left py-2.5 px-3">Entreprise</th>
                      <th className="text-left py-2.5 px-3">Contact</th>
                      <th className="text-left py-2.5 px-3">Effectifs</th>
                      <th className="text-left py-2.5 px-3">Type</th>
                      <th className="text-left py-2.5 px-3">Statut</th>
                      <th className="text-left py-2.5 px-3">Reçu le</th>
                      <th className="text-right py-2.5 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id} className="border-t border-ink-100 hover:bg-ink-50/50"
                        data-testid={`prospect-row-${p.id}`}>
                        <td className="py-3 px-3 font-medium text-ink-900">{p.companyName}</td>
                        <td className="py-3 px-3">
                          <div className="text-ink-900">{p.firstName} {p.lastName}</div>
                          <div className="text-xs text-ink-500">{p.email}</div>
                        </td>
                        <td className="py-3 px-3 text-ink-700">{HEADCOUNT_LABEL[p.employeeCount] || p.employeeCount}</td>
                        <td className="py-3 px-3">
                          {p.requestType === 'demo' ? (
                            <Badge className="bg-blue-100 text-blue-700">Démo</Badge>
                          ) : (
                            <Badge className="bg-violet-100 text-violet-700">Devis</Badge>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <Badge className={STATUS[p.status]?.cls}>{STATUS[p.status]?.label}</Badge>
                        </td>
                        <td className="py-3 px-3 text-ink-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-ink-400" /> {fmtDate(p.createdAt)}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {p.status !== 'account_created' && p.status !== 'won' && !p.convertedToAccountId ? (
                              <Button
                                size="sm"
                                onClick={() => setToConvert(p)}
                                data-testid={`convert-prospect-${p.id}`}
                                className="bg-violet-600 hover:bg-violet-700 text-white h-8 px-2.5 text-xs"
                              >
                                <UserPlus className="h-3.5 w-3.5 mr-1" /> Convertir
                              </Button>
                            ) : (
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 h-6 px-2 text-[10px]">
                                <CheckCheck className="h-3 w-3 mr-1" /> Compte créé
                              </Badge>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setSelected(p)}
                              data-testid={`view-prospect-${p.id}`}
                              className="h-8 px-2">
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <ProspectDetail prospect={selected} open={!!selected} onClose={() => setSelected(null)} onUpdate={load} />
        <ConvertProspectDialog
          prospect={toConvert}
          open={!!toConvert}
          onClose={() => setToConvert(null)}
          onConverted={load}
        />      </div>
    </DashboardLayout>
  );
}
